import { IntlShape } from 'react-intl';
import { daysUntilRelease as daysUntilReleaseDay } from '../../music24/promote/releases/releaseOrder';

/**
 * Release dates are stored as an instant at the artist's UTC midnight, so every
 * format here reads them in UTC. The same value rendered in the reader's zone
 * moves a midnight release onto the day before.
 */
const UTC = 'UTC';

const MS_PER_DAY = 86400000;

/** "12 Jun 2026" — the date column. */
export function formatCatalogDay(intl: IntlShape, isoDate: string): string {
  return intl.formatDate(isoDate, { day: 'numeric', month: 'short', year: 'numeric', timeZone: UTC });
}

/**
 * Whole calendar days from today to the release, negative once it is out. The rule of the release
 * page and Promote (`releaseOrder.daysUntilRelease`), so every screen shows one number (QA lit-9).
 */
export function daysUntilRelease(isoDate: string, now: number): number | undefined {
  if (Number.isNaN(new Date(isoDate).getTime())) {
    return undefined;
  }
  return daysUntilReleaseDay(isoDate, new Date(now)) ?? undefined;
}

/**
 * "out 63 days" / "in 20 days" / "out today".
 *
 * Written out rather than left to `formatRelativeTime`, which says "63 days
 * ago" — true of an event, wrong of a release that is still on the shelves.
 */
export function formatCatalogRelativeDay(intl: IntlShape, isoDate: string, now: number): string | undefined {
  const days = daysUntilRelease(isoDate, now);
  if (days === undefined) {
    return undefined;
  }
  if (days === 0) {
    return intl.formatMessage({ id: 'app.sml.catalog.date.today', defaultMessage: 'out today' });
  }
  if (days < 0) {
    return intl.formatMessage(
      { id: 'app.sml.catalog.date.out', defaultMessage: 'out {days, plural, one {# day} other {# days}}' },
      { days: -days }
    );
  }
  return intl.formatMessage(
    { id: 'app.sml.catalog.date.in', defaultMessage: 'in {days, plural, one {# day} other {# days}}' },
    { days }
  );
}

/** "6 days" — how long a connection request has been waiting. */
export function formatWaitingDays(intl: IntlShape, isoDate: string, now: number): string {
  const days = Math.max(0, Math.floor((now - new Date(isoDate).getTime()) / MS_PER_DAY));
  return intl.formatMessage(
    { id: 'app.sml.catalog.connection.waiting_days', defaultMessage: '{days, plural, one {# day} other {# days}}' },
    { days }
  );
}
